/**
 * Telemedicine · Phase 1 · P1.UI Phase E — web build stub for LobbyPreviewSurface.
 *
 * The native version uses `RtcSurfaceView` from `react-native-agora`, which
 * cannot be bundled for web. Metro picks `.web.tsx` over `.tsx` for the web
 * target, so this inert placeholder is what the web build sees.
 *
 * Web sponsors join through `public/meet.html`, not through the app's lobby,
 * so the on-state branch here is intentionally never exercised in production —
 * it just renders the bunker/avatar fallback regardless of `cameraOn` so the
 * page stays visually coherent if a developer ever opens the route in a
 * desktop browser.
 */
import React from 'react';
import { View, Text, StyleSheet, Image, ActivityIndicator } from 'react-native';

interface LobbyPreviewSurfaceProps {
  cameraOn: boolean;
  ready: boolean;
  firstName?: string;
  avatarUrl?: string | null;
}

const BUNKER = '#151515';
const BLUE_BLUMINE = '#015480';
const BALTIC_SEA = '#252525';
const WESTAR = '#DAD8CF';

export default function LobbyPreviewSurface({
  ready,
  firstName,
  avatarUrl,
}: LobbyPreviewSurfaceProps) {
  const initial = (firstName?.trim()?.[0] ?? '').toUpperCase();

  return (
    <View style={[styles.card, { backgroundColor: BUNKER }]}>
      {!ready ? (
        <ActivityIndicator color="#FFFFFF" />
      ) : (
        <View style={styles.avatarWrap}>
          {avatarUrl ? (
            <Image source={{ uri: avatarUrl }} style={styles.avatarImage} />
          ) : (
            <View style={[styles.avatarFallback, { backgroundColor: BLUE_BLUMINE }]}>
              {initial ? (
                <Text style={styles.avatarInitial}>{initial}</Text>
              ) : (
                <View style={[styles.avatarFallback, { backgroundColor: WESTAR }]} />
              )}
            </View>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    width: '100%',
    minHeight: 280,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  avatarWrap: {
    width: 88,
    height: 88,
    borderRadius: 64,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: BALTIC_SEA,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarImage: { width: 88, height: 88, borderRadius: 64 },
  avatarFallback: {
    width: 88,
    height: 88,
    borderRadius: 64,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitial: {
    color: '#FFFFFF',
    fontFamily: 'Sentient-Regular',
    fontSize: 36,
    lineHeight: 40,
  },
});
